import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Alert, Input, Space, Tag, Table, Modal, message, Popconfirm, Steps } from 'antd';
import { SafetyCertificateOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;

export default function SegurancaConta() {
  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [codigosRecuperacao, setCodigosRecuperacao] = useState(null);
  const [dispositivos, setDispositivos] = useState([]);
  const [modalDesativar, setModalDesativar] = useState(false);
  const [senha, setSenha] = useState('');

  const carregar = async () => {
    setCarregando(true);
    try {
      const [rs, rd] = await Promise.all([
        api.get('/2fa/status'),
        api.get('/2fa/dispositivos').catch(() => ({ data: [] })),
      ]);
      setStatus(rs.data);
      setDispositivos(rd.data || []);
    } catch (e) {
      message.error('Erro ao carregar o status da conta');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const gerarQr = async () => {
    setSalvando(true);
    try {
      const r = await api.post('/2fa/setup');
      setSetup(r.data);
      setCodigo('');
    } catch (e) {
      message.error((e.response && e.response.data && e.response.data.erro) || 'Erro ao gerar o QR code');
    } finally {
      setSalvando(false);
    }
  };

  const ativar = async () => {
    setSalvando(true);
    try {
      const r = await api.post('/2fa/ativar', { codigo: String(codigo).trim() });
      setCodigosRecuperacao(r.data.codigos_recuperacao || []);
      setSetup(null);
      await carregar();
    } catch (e) {
      message.error((e.response && e.response.data && e.response.data.erro) || 'Codigo invalido');
    } finally {
      setSalvando(false);
    }
  };

  const desativar = async () => {
    setSalvando(true);
    try {
      await api.post('/2fa/desativar', { senha });
      setModalDesativar(false);
      setSenha('');
      localStorage.removeItem('dispositivo_2fa');
      message.success('Verificacao em duas etapas desativada');
      await carregar();
    } catch (e) {
      message.error((e.response && e.response.data && e.response.data.erro) || 'Nao foi possivel desativar');
    } finally {
      setSalvando(false);
    }
  };

  const removerDispositivo = async (id) => {
    try {
      await api.delete('/2fa/dispositivos/' + id);
      await carregar();
    } catch (e) {
      message.error('Erro ao remover o dispositivo');
    }
  };

  const copiar = (texto) => {
    try {
      navigator.clipboard.writeText(texto);
      message.success('Copiado');
    } catch (e) {
      message.warning('Copie manualmente');
    }
  };

  const fmtData = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '-');

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{'Seguran\u00e7a da conta'}</Title>
        <Text type="secondary">{'Verifica\u00e7\u00e3o em duas etapas e computadores lembrados'}</Text>
      </div>

      <Card loading={carregando} style={{ marginBottom: 16 }}
        title={<span><SafetyCertificateOutlined /> {' Verifica\u00e7\u00e3o em duas etapas'}</span>}
        extra={status && (status.ativo
          ? <Tag color="green">Ativa</Tag>
          : <Tag color="default">Desativada</Tag>)}>

        {status && !status.ativo && !setup && (
          <div>
            <Paragraph>
              {'Com a verifica\u00e7\u00e3o em duas etapas, al\u00e9m da senha o sistema pede um c\u00f3digo de 6 d\u00edgitos que muda a cada 30 segundos, gerado por um aplicativo no seu celular. Assim, mesmo que algu\u00e9m descubra sua senha, n\u00e3o consegue entrar.'}
            </Paragraph>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>
              {'Voc\u00ea vai precisar de um aplicativo autenticador no celular: Google Authenticator, Microsoft Authenticator ou Authy, todos gratuitos.'}
            </Paragraph>
            <Button type="primary" onClick={gerarQr} loading={salvando}>Ativar agora</Button>
          </div>
        )}

        {setup && (
          <div>
            <Steps size="small" current={1} style={{ marginBottom: 20 }}
              items={[{ title: 'Instalar o app' }, { title: 'Ler o QR code' }, { title: 'Confirmar' }]} />
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <img src={setup.qr} alt="QR code" style={{ width: 200, height: 200, border: '1px solid #eee', borderRadius: 8 }} />
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <Paragraph>
                  {'Abra o aplicativo autenticador, escolha adicionar uma conta e aponte a c\u00e2mera para este QR code.'}
                </Paragraph>
                <Paragraph type="secondary" style={{ fontSize: 13 }}>
                  {'Se preferir digitar, use esta chave:'}
                </Paragraph>
                <Space style={{ marginBottom: 16 }}>
                  <Input readOnly value={setup.secret} style={{ width: 260, fontFamily: 'monospace' }} />
                  <Button onClick={() => copiar(setup.secret)}>Copiar</Button>
                </Space>
                <Paragraph strong>{'Agora digite o c\u00f3digo que aparece no aplicativo:'}</Paragraph>
                <Space>
                  <Input value={codigo} onChange={e => setCodigo(e.target.value)} onPressEnter={ativar}
                    placeholder="000000" maxLength={6}
                    style={{ width: 160, textAlign: 'center', fontSize: 20, letterSpacing: 4 }} />
                  <Button type="primary" onClick={ativar} loading={salvando}>Confirmar e ativar</Button>
                  <Button onClick={() => setSetup(null)}>Cancelar</Button>
                </Space>
              </div>
            </div>
          </div>
        )}

        {status && status.ativo && (
          <div>
            <Paragraph>
              {'Ativa desde ' + fmtData(status.confirmado_em) + '. Voc\u00ea tem '}
              <strong>{status.recuperacao_restantes}</strong>
              {' c\u00f3digos de recupera\u00e7\u00e3o n\u00e3o usados.'}
            </Paragraph>
            {status.recuperacao_restantes <= 2 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={'Restam poucos c\u00f3digos de recupera\u00e7\u00e3o'}
                description={'Desative e ative a verifica\u00e7\u00e3o de novo para gerar outros oito.'} />
            )}
            <Button danger onClick={() => setModalDesativar(true)}>Desativar</Button>
          </div>
        )}
      </Card>

      <Card title={'Computadores lembrados'} loading={carregando}>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          {'Nestes computadores o c\u00f3digo n\u00e3o \u00e9 pedido at\u00e9 a data de expira\u00e7\u00e3o. Remova qualquer um que voc\u00ea n\u00e3o reconhe\u00e7a ou que n\u00e3o use mais.'}
        </Paragraph>
        <Table size="small" rowKey="id" pagination={false} dataSource={dispositivos}
          locale={{ emptyText: 'Nenhum computador lembrado' }}
          columns={[
            { title: 'Navegador', dataIndex: 'descricao', ellipsis: true, render: v => v || '-' },
            { title: 'Lembrado em', dataIndex: 'criado_em', width: 170, render: fmtData },
            { title: 'Ultimo uso', dataIndex: 'ultimo_uso', width: 170, render: fmtData },
            { title: 'Expira em', dataIndex: 'expira_em', width: 170, render: fmtData },
            { title: '', width: 60, render: (_, r) => (
              <Popconfirm title={'Remover este computador?'} onConfirm={() => removerDispositivo(r.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )},
          ]} />
      </Card>

      <Modal open={!!codigosRecuperacao} closable={false} maskClosable={false} width={560}
        title={'Guarde estes c\u00f3digos de recupera\u00e7\u00e3o'}
        okText={'J\u00e1 guardei'} cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => setCodigosRecuperacao(null)}>
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message={'Esta \u00e9 a \u00fanica vez que eles aparecem'}
          description={'Imprima ou salve no seu gerenciador de senhas. Cada c\u00f3digo serve uma \u00fanica vez e permite entrar se voc\u00ea perder o celular e o acesso ao e-mail.'} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontFamily: 'monospace', fontSize: 16, marginBottom: 16 }}>
          {(codigosRecuperacao || []).map(c => <div key={c} style={{ background: '#f5f5f5', padding: '6px 10px', borderRadius: 4, textAlign: 'center' }}>{c}</div>)}
        </div>
        <Button onClick={() => copiar((codigosRecuperacao || []).join('\n'))}>Copiar todos</Button>
      </Modal>

      <Modal open={modalDesativar} title={'Desativar verifica\u00e7\u00e3o em duas etapas'}
        okText="Desativar" cancelText="Cancelar" okButtonProps={{ danger: true, loading: salvando }}
        onOk={desativar} onCancel={() => { setModalDesativar(false); setSenha(''); }}>
        <Paragraph>
          {'Sua conta volta a ser protegida apenas pela senha. Confirme com a sua senha do ERP.'}
        </Paragraph>
        <Input.Password value={senha} onChange={e => setSenha(e.target.value)} placeholder="Sua senha" onPressEnter={desativar} />
      </Modal>
    </div>
  );
}
